import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddToGroupDialog } from './AddToGroupDialog';
import { BulkEditDialog } from './BulkEditDialog';

import type { Contact } from "@/types/contact";
import type { UseBulkSelectionReturn } from "@/hooks/useBulkSelection";
import { Table } from "@tanstack/react-table";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useSetAtom, useAtomValue } from 'jotai';
import { campaignWizardAtom, contactsListAtom } from '@/lib/atoms';
import { supabase } from "@/lib/supabase";
import { captureError } from "@/lib/sentry";
import { toRpcFilters } from "@/lib/filters/rpc";

import { Users, Trash2, Mail, Pencil } from "lucide-react";

type TargetMode = 'all_filtered' | 'selected';

interface ActionBarProps {
  totalCount: number;
  appliedFilters: any;
  bulkSelection: UseBulkSelectionReturn | null;
  selectedContacts: Contact[];
  table: Table<Contact> | null;
  hasActiveFilters: boolean;
  onInvalidateCache: () => void;
}

export function ActionBar({
  totalCount,
  appliedFilters,
  bulkSelection,
  selectedContacts,
  table,
  hasActiveFilters,
  onInvalidateCache,
}: ActionBarProps) {
  const [showAddToGroup, setShowAddToGroup] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const setContactsList = useSetAtom(contactsListAtom);
  // The contacts the page has already loaded for the current filter. When this
  // holds the full match (length >= totalCount), it IS the filtered list the
  // user sees — use it directly for "all filtered" sends instead of re-deriving
  // the filter server-side (which can diverge and lacks joined email/phone).
  const contactsList = useAtomValue(contactsListAtom);
  const [isDeleting, setIsDeleting] = useState(false);
  const setCampaignWizard = useSetAtom(campaignWizardAtom);
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspaces();

  const hasSelection = bulkSelection && bulkSelection.effectiveCount > 0;

  // Determine default target mode
  const getDefaultTargetMode = (): TargetMode => {
    if (hasSelection) return 'selected';
    if (hasActiveFilters) return 'all_filtered';
    return 'all_filtered';
  };

  const [targetMode, setTargetMode] = useState<TargetMode>(getDefaultTargetMode);

  // Update target mode when selection state changes
  useEffect(() => {
    if (hasSelection) {
      setTargetMode('selected');
    } else if (hasActiveFilters) {
      setTargetMode('all_filtered');
    }
  }, [hasSelection, hasActiveFilters]);

  // Build a synthetic bulkSelection for "all_filtered" mode
  const syntheticBulkSelection: UseBulkSelectionReturn = {
    mode: 'all_matching',
    selectedIds: new Set<string>(),
    excludedIds: new Set<string>(),
    filterState: appliedFilters,
    totalMatchingCount: totalCount,
    effectiveCount: totalCount,
    toggleId: () => {},
    selectAll: () => {},
    clearSelection: () => {},
    isSelected: () => false,
    setExplicitSelection: () => {},
  };

  // Get the effective bulkSelection based on target mode
  const getEffectiveBulkSelection = (): UseBulkSelectionReturn => {
    if (targetMode === 'all_filtered') {
      return syntheticBulkSelection;
    }
    return bulkSelection!;
  };

  const getEffectiveContacts = (): Contact[] => {
    if (targetMode === 'all_filtered') {
      return [];
    }
    return selectedContacts;
  };

  const getEffectiveCount = (): number => {
    if (targetMode === 'all_filtered') {
      return totalCount;
    }
    return bulkSelection?.effectiveCount ?? 0;
  };

  // Delete handler (copied from ContactTableToolbar)
  const deleteSelectedContacts = useCallback(async () => {
    if (isDeleting || !currentWorkspace) return;

    try {
      setIsDeleting(true);
      const effectiveBulk = getEffectiveBulkSelection();

      let selectedIds: string[];

      if (effectiveBulk.mode === 'all_matching') {
        const filters = effectiveBulk.filterState || appliedFilters;
        const { data: matchedIds, error: filterError } = await supabase
          .rpc('get_filtered_contact_ids', {
            // Translate to RPC filter shape (snake_case keys) so date/relative
            // conditions resolve identically to the visible list — passing the
            // raw client shape silently dropped some, which for a DELETE could
            // remove a broader set than the filter intended.
            p_workspace_id: currentWorkspace.id,
            p_filters: filters ? toRpcFilters(filters) : null,
          })
          .range(0, 99999);

        if (filterError) throw new Error(`Failed to resolve contacts: ${filterError.message}`);

        selectedIds = (matchedIds || [])
          .map((row: { contact_id: string }) => row.contact_id)
          .filter((id: string) => !effectiveBulk.excludedIds.has(id));
      } else {
        selectedIds = Array.from(effectiveBulk.selectedIds);
      }

      if (selectedIds.length === 0) {
        toast({
          title: "No contacts to delete",
          description: "No contacts are selected for deletion.",
          variant: "destructive",
        });
        return;
      }

      const { data: result, error } = await supabase.rpc('bulk_delete_contacts', {
        p_contact_ids: selectedIds,
        p_workspace_id: currentWorkspace.id,
      });

      if (error) throw error;

      const deletedCount = result?.deleted_count ?? selectedIds.length;
      toast({
        description: `${deletedCount} contact${deletedCount > 1 ? 's' : ''} deleted successfully`,
      });

      // Optimistically remove deleted contacts from the list immediately
      const deletedIdSet = new Set(selectedIds);
      setContactsList((prev) => ({
        ...prev,
        contacts: prev.contacts.filter((c) => !deletedIdSet.has(c.id)),
        totalCount: Math.max(0, prev.totalCount - selectedIds.length),
      }));

      // Clear selection after delete — don't invalidate cache since
      // the optimistic remove above already has the correct state.
      // Calling onInvalidateCache/onRowsChange would clear the list
      // to [] and refetch, causing a visible flicker.
      if (bulkSelection) {
        bulkSelection.clearSelection();
      }
      if (table) {
        table.toggleAllRowsSelected(false);
      }

    } catch (err) {
      console.error('Error deleting contacts:', err);
      captureError(err as Error, { feature: 'bulk-delete' });
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to delete contacts. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [isDeleting, currentWorkspace, targetMode, bulkSelection, appliedFilters, table, toast, onInvalidateCache, totalCount]);

  const handleAddToGroup = () => setShowAddToGroup(true);
  const handleBulkEdit = () => setShowBulkEdit(true);

  const handleSendEmail = () => {
    // Fast path for "all filtered" when the page has already loaded every
    // matching contact: hand the wizard the exact list the user sees. These
    // rows come from contacts_with_primary_details (so they carry
    // primary_email/phone) and are correctly filtered (incl. group/list
    // filters), so no server-side re-resolution is needed — explicit mode means
    // the wizard uses them as-is instead of calling resolveBulkSelection().
    if (
      targetMode === 'all_filtered' &&
      contactsList.totalCount > 0 &&
      contactsList.contacts.length >= contactsList.totalCount
    ) {
      setCampaignWizard({
        open: true,
        selectedContacts: contactsList.contacts,
        bulkSelection: {
          ...syntheticBulkSelection,
          mode: 'explicit',
          selectedIds: new Set(contactsList.contacts.map((c) => c.id)),
          effectiveCount: contactsList.contacts.length,
        },
      });
      return;
    }
    // Otherwise (a sub-segment via checkboxes, or a filter matching more rows
    // than are currently loaded) keep the existing behavior — the wizard
    // resolves the full matching set server-side.
    const effectiveBulk = getEffectiveBulkSelection();
    const contacts = getEffectiveContacts();
    setCampaignWizard({
      open: true,
      selectedContacts: contacts,
      bulkSelection: effectiveBulk,
    });
  };

  const handleDelete = () => setShowDeleteDialog(true);

  const handleSuccess = () => {
    if (bulkSelection) {
      bulkSelection.clearSelection();
    }
    if (table) {
      table.toggleAllRowsSelected(false);
      if (table.options.meta?.onRowsChange) {
        table.options.meta.onRowsChange();
      }
    }
    onInvalidateCache();
  };

  const effectiveCount = getEffectiveCount();

  return (
    <>
      <div className="animate-in slide-in-from-bottom-2 fade-in duration-200 flex items-center gap-2 py-2">
        {/* Target toggle */}
        <Select value={targetMode} onValueChange={(v) => setTargetMode(v as TargetMode)}>
          <SelectTrigger className="h-8 w-auto min-w-[180px] text-xs font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {hasActiveFilters && (
              <SelectItem value="all_filtered">
                All {totalCount.toLocaleString()} filtered
              </SelectItem>
            )}
            {hasSelection && (
              <SelectItem value="selected">
                {bulkSelection!.effectiveCount} selected
              </SelectItem>
            )}
            {!hasActiveFilters && !hasSelection && (
              <SelectItem value="all_filtered">
                All {totalCount.toLocaleString()} contacts
              </SelectItem>
            )}
          </SelectContent>
        </Select>

        {/* Separator */}
        <div className="h-6 w-px bg-border" />

        {/* Action buttons */}
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleAddToGroup}>
          <Users className="h-3.5 w-3.5 mr-1.5" />
          Add to Group
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleBulkEdit}>
          <Pencil className="h-3.5 w-3.5 mr-1.5" />
          Bulk Edit
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleSendEmail}>
          <Mail className="h-3.5 w-3.5 mr-1.5" />
          Send Email
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs text-destructive"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          {isDeleting ? "Deleting..." : "Delete"}
        </Button>
      </div>

      {/* Dialogs */}
      <AddToGroupDialog
        open={showAddToGroup}
        onOpenChange={setShowAddToGroup}
        selectedContacts={getEffectiveContacts()}
        bulkSelection={getEffectiveBulkSelection()}
        onSuccess={handleSuccess}
      />

      <BulkEditDialog
        open={showBulkEdit}
        onOpenChange={setShowBulkEdit}
        selectedContacts={getEffectiveContacts()}
        bulkSelection={getEffectiveBulkSelection()}
        requireConfirmation={targetMode === 'all_filtered'}
        onSuccess={handleSuccess}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contacts</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {effectiveCount} contact{effectiveCount !== 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteSelectedContacts}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
